import React from 'react';
import { orderStatus } from '../Wallet/services/walletService';
import { CheckCircle2, XCircle, AlertTriangle, Loader2, RefreshCw } from 'lucide-react';
import '../../../../styles/components/payment-result.scss';

/**
 * Rendered at the route the backend redirects to after processing the
 * SmartPay callback, e.g. /payment/result/:orderId
 *
 * react-router v5: register as <Route path="/payment/result/:orderId" component={PaymentResult} />
 *   -> orderId is available at this.props.match.params.orderId
 *
 * react-router v6 (no match prop passed to class components): wrap it —
 *   function PaymentResultRoute() {
 *     const params = useParams();
 *     return <PaymentResult match={{ params }} />;
 *   }
 *   and route to <PaymentResultRoute /> instead.
 *
 * The status query param in the URL is only used for the first paint;
 * the real, trusted status always comes from GET /payment/status/:orderId.
 */

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 2000;

const TYPE_LABELS = {
  wallet_recharge: {
    success: 'Your wallet has been credited.',
    action: { label: 'Go to wallet', href: '/wallet-manage' },
  },
  auction_enrollment: {
    success: 'Your enrollment is confirmed. You can start bidding.',
    action: { label: 'View auction', href: null }, // filled in from txn.reference_id if available
  },
  auction_payment: {
    success: 'Your auction payment has been received.',
    action: { label: 'Go to auction', href: null },
  },
};
const FAILED_SUBTITLE_BY_TYPE = {
  wallet_recharge:
    'Your wallet recharge was not successful. No amount has been added to your wallet.',
  auction_enrollment: 'Your enrollment payment was not successful. No enrollment has been applied.',
  auction_payment: 'Your payment was not successful. No credit has been applied to your account.',
};

const DEFAULT_FAILED_SUBTITLE =
  'Your payment was not successful. No enrollment or credit has been applied.';

function getFailedSubtitle(type) {
  return FAILED_SUBTITLE_BY_TYPE[type] || DEFAULT_FAILED_SUBTITLE;
}

function getBackLink(txn) {
  switch (txn?.type) {
    case 'wallet_recharge':
      return '/wallet';
    case 'auction_enrollment':
      return txn?.reference_id ? `/auction-details/${txn.reference_id}` : '/auction';
    case 'auction_payment':
      return '/mzadcom-payment';
    default:
      return '/';
  }
}

function getBackLabel(type) {
  switch (type) {
    case 'wallet_recharge':
      return 'Back to wallet';
    case 'auction_enrollment':
      return 'Back to auction';
    case 'auction_payment':
      return 'Back to auction payments';
    default:
      return 'Back to home';
  }
}

function formatAmount(amount) {
  const n = Number(amount);
  if (Number.isNaN(n)) return amount;
  return n.toLocaleString('en-OM', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}

class PaymentResult extends React.Component {
  constructor(props) {
    super(props);
    const path = window.location.pathname.replace(/\/+$/, ''); // strip trailing slash(es)
    const orderId = path.substring(path.lastIndexOf('/') + 1);
    this.state = {
      phase: 'checking', // checking | success | failed | error
      txn: null,
      errorMessage: null,
      retryCount: 0,
      orderId,
    };
    this._mounted = false;
  }

  componentDidMount() {
    this._mounted = true;
    this.verifyStatus();
  }

  componentWillUnmount() {
    this._mounted = false;
    clearTimeout(this._retryTimer);
  }

  verifyStatus = async () => {
    const { orderId } = this.state;

    if (!orderId) {
      this.setState({ phase: 'error', errorMessage: 'No order reference was provided.' });
      return;
    }

    try {
      const data = await orderStatus(orderId);
      console.log(data, 'res summary');

      if (!this._mounted) return;

      const isSuccess = Boolean(data.is_success);

      this.setState({
        phase: isSuccess ? 'success' : 'failed',
        txn: data,
      });
    } catch (err) {
      console.log(err, 'err');

      if (!this._mounted) return;

      const { retryCount } = this.state;

      if (retryCount < MAX_RETRIES) {
        this._retryTimer = setTimeout(() => {
          if (!this._mounted) return;
          this.setState((prev) => ({ retryCount: prev.retryCount + 1 }), this.verifyStatus);
        }, RETRY_DELAY_MS);
        return;
      }

      this.setState({
        phase: 'error',
        errorMessage:
          err.response?.data?.message ||
          'We could not verify your payment right now. It may still have gone through — please check again shortly.',
      });
    }
  };

  handleManualRetry = () => {
    this.setState({ phase: 'checking', errorMessage: null, retryCount: 0 }, this.verifyStatus);
  };

  getTypeInfo(type) {
    return TYPE_LABELS[type] || { success: 'Your payment has been processed.', action: null };
  }

  renderChecking() {
    const { retryCount } = this.state;
    return (
      <div className="pr-card pr-card--pending" role="status" aria-live="polite">
        <div className="pr-icon pr-icon--spin">
          <Loader2 size={40} strokeWidth={2} />
        </div>
        <h1 className="pr-title">Verifying your payment</h1>
        <p className="pr-subtitle">
          {retryCount > 0
            ? `Still checking — this is taking a little longer than usual (attempt ${retryCount + 1} of ${MAX_RETRIES + 1})…`
            : 'Please wait a moment while we confirm this with the payment gateway.'}
        </p>
        <div className="pr-progress" aria-hidden="true">
          <div className="pr-progress__bar" />
        </div>
      </div>
    );
  }

  renderSuccess() {
    const { txn } = this.state;
    const typeInfo = this.getTypeInfo(txn.type);

    return (
      <div className="pr-card pr-card--success">
        <div className="pr-icon pr-icon--success">
          <CheckCircle2 size={40} strokeWidth={2} />
        </div>
        <h1 className="pr-title">Payment successful</h1>
        <p className="pr-subtitle">{typeInfo.success}</p>

        <dl className="pr-summary">
          {txn.tracking_id && (
            <div className="pr-summary__row">
              <dt>Reference</dt>
              <dd>{txn.tracking_id}</dd>
            </div>
          )}
          {txn.order_id && (
            <div className="pr-summary__row">
              <dt>Order ID</dt>
              <dd>{txn.order_id}</dd>
            </div>
          )}
          {txn.amount != null && (
            <div className="pr-summary__row">
              <dt>Amount</dt>
              <dd>OMR {formatAmount(txn.amount)}</dd>
            </div>
          )}
        </dl>

        <div className="pr-actions">
          {/* <a href="/" className="pr-btn pr-btn--primary">
            Back to home <ArrowRight size={16} />
          </a> */}
          <a href={getBackLink(txn)} className="pr-btn pr-btn--primary">
            {getBackLabel(txn?.type)}
          </a>
        </div>
      </div>
    );
  }

  renderFailed() {
    const { txn } = this.state;

    return (
      <div className="pr-card pr-card--failed">
        <div className="pr-icon pr-icon--failed">
          <XCircle size={40} strokeWidth={2} />
        </div>
        <h1 className="pr-title">Payment not completed</h1>
        <p className="pr-subtitle">{getFailedSubtitle(txn?.type)}</p>

        <dl className="pr-summary">
          {txn?.order_id && (
            <div className="pr-summary__row">
              <dt>Order ID</dt>
              <dd>{txn.order_id}</dd>
            </div>
          )}
          <div className="pr-summary__row">
            <dt>Status</dt>
            <dd>{txn.status_label}</dd>
          </div>
        </dl>

        <div className="pr-actions">
          <a href={getBackLink(txn)} className="pr-btn pr-btn--ghost">
            {getBackLabel(txn?.type)}
          </a>

          {/* <button type="button" className="pr-btn pr-btn--primary" onClick={this.handleManualRetry}>
            <RefreshCw size={16} /> Try again
          </button> */}
        </div>
      </div>
    );
  }

  renderError() {
    const { errorMessage } = this.state;

    return (
      <div className="pr-card pr-card--error">
        <div className="pr-icon pr-icon--error">
          <AlertTriangle size={40} strokeWidth={2} />
        </div>
        <h1 className="pr-title">Couldn't verify this payment</h1>
        <p className="pr-subtitle">{errorMessage}</p>

        <div className="pr-actions">
          <a href="/" className="pr-btn pr-btn--ghost">
            Back to home
          </a>
          <button type="button" className="pr-btn pr-btn--primary" onClick={this.handleManualRetry}>
            <RefreshCw size={16} /> Check again
          </button>
        </div>
      </div>
    );
  }

  render() {
    const { phase } = this.state;

    return (
      <div className="pr-page">
        <div className="pr-container">
          {phase === 'checking' && this.renderChecking()}
          {phase === 'success' && this.renderSuccess()}
          {phase === 'failed' && this.renderFailed()}
          {phase === 'error' && this.renderError()}
        </div>
      </div>
    );
  }
}

export default PaymentResult;
